import { z } from 'zod'

import type {
  PopupFrequency,
  StorePopup,
} from '@/app/services/popup.service'

const allowedImageTypes = ['image/jpeg', 'image/png', 'image/webp']
const maxImageSize = 5 * 1024 * 1024

export function isValidStorefrontUrl(value: string): boolean {
  if (!value) return true
  if (value.startsWith('/') && !value.startsWith('//')) return true

  try {
    const url = new URL(value)
    return url.protocol === 'http:' || url.protocol === 'https:'
  } catch {
    return false
  }
}

function validDate(value: string): boolean {
  return value === '' || !Number.isNaN(new Date(value).getTime())
}

export const popupFormSchema = z
  .object({
    name: z
      .string()
      .trim()
      .min(1, 'El nombre es obligatorio')
      .max(255, 'El nombre no puede superar los 255 caracteres'),
    image: z.custom<File | null>((value) => value === null || value instanceof File),
    current_image_url: z.string(),
    call_to_action_url: z
      .string()
      .trim()
      .max(2048, 'La URL no puede superar los 2048 caracteres')
      .refine(isValidStorefrontUrl, 'Ingresa una ruta interna o una URL HTTP/HTTPS válida'),
    sort_order: z.string(),
    is_active: z.boolean(),
    starts_at: z.string().refine(validDate, 'La fecha de inicio no es válida'),
    ends_at: z.string().refine(validDate, 'La fecha de fin no es válida'),
    show_frequency: z.enum(['ALWAYS', 'ONCE', 'EVERY_N_DAYS']),
    show_again_after_days: z.string(),
  })
  .superRefine((values, context) => {
    if (!values.image && !values.current_image_url) {
      context.addIssue({
        code: 'custom',
        path: ['image'],
        message: 'Selecciona una imagen',
      })
    }

    if (values.image) {
      if (!allowedImageTypes.includes(values.image.type)) {
        context.addIssue({
          code: 'custom',
          path: ['image'],
          message: 'La imagen debe ser JPG, PNG o WebP',
        })
      }
      if (values.image.size > maxImageSize) {
        context.addIssue({
          code: 'custom',
          path: ['image'],
          message: 'La imagen no puede superar los 5 MB',
        })
      }
    }

    const sortOrder = Number(values.sort_order)
    if (!Number.isInteger(sortOrder) || sortOrder < 0) {
      context.addIssue({
        code: 'custom',
        path: ['sort_order'],
        message: 'El orden debe ser un entero mayor o igual a 0',
      })
    }

    if (values.starts_at && values.ends_at) {
      const startsAt = new Date(values.starts_at)
      const endsAt = new Date(values.ends_at)
      if (endsAt <= startsAt) {
        context.addIssue({
          code: 'custom',
          path: ['ends_at'],
          message: 'La fecha de fin debe ser posterior al inicio',
        })
      }
    }

    if (values.show_frequency === 'EVERY_N_DAYS') {
      const days = Number(values.show_again_after_days)
      if (!Number.isInteger(days) || days < 1) {
        context.addIssue({
          code: 'custom',
          path: ['show_again_after_days'],
          message: 'Indica un número de días mayor o igual a 1',
        })
      }
    }
  })

export type PopupFormValues = z.infer<typeof popupFormSchema>

export const emptyPopupForm: PopupFormValues = {
  name: '',
  image: null,
  current_image_url: '',
  call_to_action_url: '',
  sort_order: '0',
  is_active: true,
  starts_at: '',
  ends_at: '',
  show_frequency: 'ALWAYS',
  show_again_after_days: '',
}

export const popupFrequencyLabels: Record<PopupFrequency, string> = {
  ALWAYS: 'Siempre',
  ONCE: 'Una sola vez',
  EVERY_N_DAYS: 'Cada N días',
}

export function toDateInput(value: string | null): string {
  if (!value) return ''
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return ''
  return value.split('T')[0]
}

export function popupToFormValues(popup: StorePopup): PopupFormValues {
  return {
    name: popup.name,
    image: null,
    current_image_url: popup.image_url,
    call_to_action_url: popup.call_to_action_url ?? '',
    sort_order: popup.sort_order.toString(),
    is_active: popup.is_active,
    starts_at: toDateInput(popup.starts_at),
    ends_at: toDateInput(popup.ends_at),
    show_frequency: popup.show_frequency,
    show_again_after_days: popup.show_again_after_days?.toString() ?? '',
  }
}

export function popupFormToFormData(values: PopupFormValues): FormData {
  const data = new FormData()
  data.append('name', values.name.trim())
  if (values.image) data.append('image', values.image)
  data.append('call_to_action_url', values.call_to_action_url.trim())
  data.append('sort_order', String(Number(values.sort_order)))
  data.append('is_active', values.is_active ? '1' : '0')
  data.append('starts_at', values.starts_at)
  data.append('ends_at', values.ends_at)
  data.append('show_frequency', values.show_frequency)
  data.append(
    'show_again_after_days',
    values.show_frequency === 'EVERY_N_DAYS'
      ? String(Number(values.show_again_after_days))
      : '',
  )

  return data
}
